import { useEffect, useRef, useState } from "react";
import type { PatientGroup } from "@care-by-ella/contracts";
import { fetchMedicalEvidence, UnauthorizedError } from "@/lib/clinician-api";
import {
  requestedCompletionRows,
  type RequestedCompletionRow,
} from "@/lib/completion-round";
import type { TransferReviewState } from "@/lib/evidence-status";
import { EvidenceSummaryCard } from "./EvidenceSummaryCard";
import { StatusTag } from "./StatusTag";

// TES-1329 (TES-1327 §6, Emelie 2026-10-13) — the patient overview's one
// evidence card:
//   "Den samlade rubriken på patientöversikten ska vara: Underlag och kompletteringar"
//   "1. Underlag inför bedömningen. 2. Begärda kompletteringar."
// The first part is what she handed over before the doctor's assessment,
// read by her journey (EvidenceSummaryCard with `patientGroup`); the second is
// only what the doctor has asked for since, each item with its own status.

export function UnderlagOchKompletteringar({
  patientId,
  patientGroup,
  transferReview,
  reloadKey = 0,
  onUnauthorized,
}: {
  patientId: string;
  patientGroup: PatientGroup;
  transferReview: TransferReviewState | null;
  // Bumped when a komplettering is requested from the overview.
  reloadKey?: number;
  onUnauthorized: () => void;
}) {
  const [requested, setRequested] = useState<RequestedCompletionRow[] | null>(
    null,
  );
  const [error, setError] = useState(false);
  // Through a ref so a parent re-render does not refetch: every fetch here is
  // a logged patient read.
  const onUnauthorizedRef = useRef(onUnauthorized);
  onUnauthorizedRef.current = onUnauthorized;

  useEffect(() => {
    let live = true;
    setError(false);
    fetchMedicalEvidence(patientId)
      .then((d) => {
        if (live) setRequested(requestedCompletionRows(d));
      })
      .catch((e: unknown) => {
        if (!live) return;
        if (e instanceof UnauthorizedError) onUnauthorizedRef.current();
        else setError(true);
      });
    return () => {
      live = false;
    };
  }, [patientId, reloadKey]);

  return (
    <section
      className="rounded-md border border-surfaceMuted bg-surface p-md"
      data-testid="underlag-och-kompletteringar"
    >
      <h2 className="mb-sm font-display text-lg">
        Underlag och kompletteringar
      </h2>
      <div className="flex flex-col gap-md">
        <section aria-labelledby="underlag-heading">
          <h3
            id="underlag-heading"
            className="mb-xs text-sm font-semibold text-textPrimary"
          >
            Underlag inför bedömningen
          </h3>
          <EvidenceSummaryCard
            patientId={patientId}
            onUnauthorized={onUnauthorized}
            patientGroup={patientGroup}
            transferReview={transferReview}
            bare
          />
        </section>
        <section
          aria-labelledby="requested-completions-heading"
          data-testid="requested-completions"
        >
          <h3
            id="requested-completions-heading"
            className="mb-xs text-sm font-semibold text-textPrimary"
          >
            Begärda kompletteringar
          </h3>
          {error ? (
            <p className="text-sm text-danger">
              Kunde inte hämta kompletteringarna.
            </p>
          ) : requested === null ? (
            <p className="text-sm text-textSecondary">Hämtar…</p>
          ) : requested.length === 0 ? (
            <p className="text-sm text-textSecondary">
              Inga öppna begärda kompletteringar.
            </p>
          ) : (
            <dl className="flex flex-col gap-sm">
              {requested.map((r) => (
                <div key={r.key} className="flex flex-col gap-[2px]">
                  <dt className="text-xs text-textSecondary">{r.label}</dt>
                  <dd className="text-sm text-textPrimary">
                    <StatusTag tone={r.tone}>{r.state}</StatusTag>
                    {r.detail ? (
                      <span className="mt-[2px] block text-xs text-textSecondary">
                        {r.detail}
                      </span>
                    ) : null}
                  </dd>
                </div>
              ))}
            </dl>
          )}
        </section>
      </div>
    </section>
  );
}
